import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Minus, X, Radio, Maximize2, Minimize2, Pin, PinOff, Camera, Mic, ChevronDown, Monitor, Circle, Square, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

let scriptCache = {};
const loadJitsi = (domain, appId) => {
  // JaaS requires the tenant-scoped external_api.js: https://8x8.vc/<APP_ID>/external_api.js
  // meet.jit.si uses https://meet.jit.si/external_api.js
  const src = appId
    ? `https://${domain}/${appId}/external_api.js`
    : `https://${domain}/external_api.js`;
  if (window.JitsiMeetExternalAPI && scriptCache[src]) return Promise.resolve();
  if (scriptCache[src]) return scriptCache[src];
  scriptCache[src] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return scriptCache[src];
};

// Floating / full-screen embedded meeting so the dashboard stays usable during the call.
// P2P and small-group HD tuned: 720p floor, 1080p ceiling, VP9, adaptive simulcast.
const isMobileViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
const canScreenShareLocal = () =>
  typeof navigator !== 'undefined'
  && navigator.mediaDevices
  && typeof navigator.mediaDevices.getDisplayMedia === 'function';

export const JitsiRoom = ({ room, displayName, title, domain = 'meet.jit.si', onClose }) => {
  const frameRef = useRef(null);
  const apiRef = useRef(null);
  // `onClose` prop is often passed as an inline arrow (`() => setJoined(false)`)
  // by parents — that's a new function ref every parent render. If we put it
  // in the boot-effect's deps we tear down the entire Jitsi iframe on every
  // parent re-render (the 5-second reset loop reported by users). Route it
  // through a ref so we always call the latest closure without a rebuild.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const [minimized, setMinimized] = useState(false);
  const [fullscreen, setFullscreen] = useState(() => isMobileViewport()); // phones start full-screen
  const [pinned, setPinned] = useState(false);
  const [sharingScreen, setSharingScreen] = useState(false);
  const [myId, setMyId] = useState(null);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [devices, setDevices] = useState({ videoInput: [], audioInput: [] });
  const [selectedCam, setSelectedCam] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [tokenState, setTokenState] = useState({ ready: false, error: null, jwt: null, appId: null, domain });
  const canRecord = canScreenShareLocal(); // hide REC button on mobile; iOS/Android can't screen-capture

  // Local class recording (MediaRecorder over the picked tab/window, mixed with mic)
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordStreamsRef = useRef([]);

  const refreshDevices = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    api.getAvailableDevices().then((d) => {
      setDevices({
        videoInput: d.videoInput || [],
        audioInput: d.audioInput || [],
      });
    }).catch(() => {});
    api.getCurrentDevices().then((d) => {
      setSelectedCam(d?.videoInput?.deviceId || '');
      setSelectedMic(d?.audioInput?.deviceId || '');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let disposed = false;

    const boot = async () => {
      // 1. Ask backend for a JaaS token (short-lived, room-scoped)
      let jwtToken = null;
      let effectiveDomain = domain;
      let appId = null;
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('academy_token');
        const r = await axios.post(`${base}/api/academy/live/token`, { room }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.data?.jaas_enabled) {
          jwtToken = r.data.jwt;
          effectiveDomain = r.data.domain || '8x8.vc';
          appId = r.data.app_id;
        }
      } catch (err) {
        // Fall through to meet.jit.si if the JaaS mint failed — better than a black screen
        console.warn('JaaS token unavailable — falling back to meet.jit.si', err?.response?.data || err?.message);
      }
      if (disposed) return;
      setTokenState({ ready: true, error: null, jwt: jwtToken, appId, domain: effectiveDomain });

      // 2. Load the correct external_api.js (tenant-scoped for JaaS)
      try {
        await loadJitsi(effectiveDomain, appId);
      } catch {
        if (!disposed) toast.error('Could not load Jitsi. Check your connection.');
        return;
      }
      if (disposed || !frameRef.current) return;

      // 3. Build the room name — JaaS requires <APP_ID>/<room>
      const roomName = appId ? `${appId}/${room}` : room;

      const api = new window.JitsiMeetExternalAPI(effectiveDomain, {
          roomName,
          ...(jwtToken ? { jwt: jwtToken } : {}),
          parentNode: frameRef.current,
          userInfo: { displayName: displayName || 'Scholar' },
          configOverwrite: {
            startWithAudioMuted: true,
            // JaaS uses prejoinConfig.enabled; legacy Jitsi uses prejoinPageEnabled.
            // Set BOTH so we're forced straight into the room on 8x8.vc + meet.jit.si.
            prejoinConfig: { enabled: false, hideDisplayName: false },
            prejoinPageEnabled: false,
            requireDisplayName: false,
            disableDeepLinking: true,
            // HD video constraints — force a 720p floor so viewers see a crisp image
            resolution: 720,
            constraints: {
              video: {
                height: { ideal: 720, max: 1080, min: 480 },
                width: { ideal: 1280, max: 1920, min: 640 },
                frameRate: { ideal: 30, min: 15 },
                aspectRatio: 16 / 9,
              },
            },
            // Codec + bitrate targets for classroom-quality video
            videoQuality: {
              preferredCodec: 'VP9',
              maxBitratesVideo: {
                low: 300000,
                standard: 800000,
                high: 2500000,
              },
            },
            // Simulcast + adaptive layers so 20+ viewers each get the best layer their pipe can handle
            disableSimulcast: false,
            channelLastN: -1,
            startVideoMuted: 0,
            // Better spotlight behaviour when a presenter is pinned
            disableTileEnlargement: false,
          },
          interfaceConfigOverwrite: {
            MOBILE_APP_PROMO: false,
            SHOW_JITSI_WATERMARK: false,
            VIDEO_QUALITY_LABEL_DISABLED: false,
          },
      });
      apiRef.current = api;
      // Route close events through a ref so we don't need `onClose` in the
      // effect's dep array (which would remount the entire iframe every
      // parent re-render — the 5-second reset loop).
      api.addListener('readyToClose', () => { const cb = onCloseRef.current; if (cb) cb(); });
      api.addListener('videoConferenceJoined', (e) => {
        setMyId(e.id);
        setTimeout(refreshDevices, 500);
        // Nudge the host to press Record so the class actually gets saved to
        // the Teacher Dashboard · Class Recordings panel. Classes aren't
        // recorded automatically — a lesson we learned the hard way.
        if (canRecord) {
          setTimeout(() => {
            toast.info('Tap the red "Record" button in the toolbar to save this class to the Class Recordings panel.', { duration: 10000 });
          }, 2500);
        }
      });
      api.addListener('deviceListChanged', refreshDevices);
      api.addListener('screenSharingStatusChanged', (e) => setSharingScreen(!!e.on));
    };

    boot();
    return () => {
      disposed = true;
      try { apiRef.current && apiRef.current.dispose(); } catch (e) { /* noop */ }
    };
    // NOTE: `onClose` intentionally omitted — accessed via onCloseRef so an
    // unstable parent closure can never tear down the live iframe. Any real
    // change to room/domain/displayName SHOULD reboot (rare).
  }, [room, domain, displayName, refreshDevices]);

  const togglePin = () => {
    const api = apiRef.current;
    if (!api || !myId) return;
    if (pinned) {
      api.executeCommand('pinParticipant', null);
      setPinned(false);
    } else {
      // Pin self so all viewers see the presenter's tile enlarged
      api.executeCommand('pinParticipant', myId);
      setPinned(true);
    }
  };

  const pickCamera = (deviceId, label) => {
    const api = apiRef.current;
    if (!api) return;
    api.setVideoInputDevice(label, deviceId);
    setSelectedCam(deviceId);
    setDevicesOpen(false);
  };

  const pickMic = (deviceId, label) => {
    const api = apiRef.current;
    if (!api) return;
    api.setAudioInputDevice(label, deviceId);
    setSelectedMic(deviceId);
    setDevicesOpen(false);
  };

  const toggleScreenShare = () => {
    const api = apiRef.current;
    if (!api) return;
    api.executeCommand('toggleShareScreen');
  };

  const stopRecordStreams = () => {
    (recordStreamsRef.current || []).forEach((s) => {
      try { s.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ }
    });
    recordStreamsRef.current = [];
  };

  const uploadRecording = async (blob) => {
    const token = localStorage.getItem('academy_token');
    if (!token) {
      // Fall back to local download if not authed
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `saa-class-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
      a.click();
      URL.revokeObjectURL(url);
      toast.info('Not signed in — recording saved to your Downloads folder.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const niceTitle = (title || 'Live Class').trim();
      const filename = `saa-class-${niceTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${stamp}.webm`;
      fd.append('file', new File([blob], filename, { type: 'video/webm' }));
      fd.append('title', `${niceTitle} · ${new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
      if (room) fd.append('room', room);
      const base = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${base}/api/live/recording/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      toast.success('Class recording saved to the Teacher Dashboard · Class Recordings.');
    } catch (err) {
      // On failure, still hand the file to the user so it's not lost
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `saa-class-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
      a.click();
      URL.revokeObjectURL(url);
      const msg = err?.response?.data?.detail || err?.message || 'Vault upload failed';
      toast.error(`${msg} — file saved locally as backup.`);
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    if (recording) return;
    if (!canScreenShareLocal()) {
      toast.info('Class recording needs a laptop or desktop browser.');
      return;
    }
    try {
      // 1. Screen/tab capture — user picks the Jitsi tab or "Entire Screen"
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30 }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true, // system / tab audio if the browser supports + user opts in
      });
      // 2. Mic capture — so the coach's live voice is on the recording
      let mic = null;
      try {
        mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) { /* mic optional */ }

      recordStreamsRef.current = [display, mic].filter(Boolean);

      // 3. Mix the two audio tracks (tab audio + mic) into one via WebAudio
      let combinedStream = display;
      const audioTracks = [];
      if (display.getAudioTracks().length > 0) audioTracks.push(...display.getAudioTracks());
      if (mic && mic.getAudioTracks().length > 0) audioTracks.push(...mic.getAudioTracks());
      if (audioTracks.length > 1) {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const dest = ctx.createMediaStreamDestination();
        audioTracks.forEach((t) => {
          const src = ctx.createMediaStreamSource(new MediaStream([t]));
          src.connect(dest);
        });
        combinedStream = new MediaStream([
          ...display.getVideoTracks(),
          ...dest.stream.getAudioTracks(),
        ]);
      } else if (audioTracks.length === 1 && audioTracks[0].id !== display.getAudioTracks()[0]?.id) {
        combinedStream = new MediaStream([...display.getVideoTracks(), ...audioTracks]);
      }

      // 4. Pick the best supported codec
      const mimeCandidates = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
      ];
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) || 'video/webm';

      const rec = new MediaRecorder(combinedStream, { mimeType, videoBitsPerSecond: 3_000_000 });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopRecordStreams();
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        if (blob.size > 0) await uploadRecording(blob);
      };
      // If the user hits the browser's own "Stop sharing" button, stop cleanly.
      display.getVideoTracks()[0]?.addEventListener('ended', () => {
        if (rec.state !== 'inactive') rec.stop();
        setRecording(false);
      });
      rec.start(1000); // gather in 1s slices so nothing is lost on crash
      recorderRef.current = rec;
      setRecording(true);
      toast.success('Recording — pick the meeting tab in the picker for best quality.');
    } catch (err) {
      stopRecordStreams();
      const msg = err?.message || 'Screen capture was blocked.';
      toast.error(msg);
    }
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    } else {
      stopRecordStreams();
    }
    setRecording(false);
  };

  // Layout: minimized > fullscreen > floating (default)
  const containerStyle = minimized
    ? { width: 260, height: 52, bottom: 16, right: 16 }
    : fullscreen
      ? { top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }
      : { width: 'min(720px, 94vw)', height: 'min(480px, 72vh)', bottom: 16, right: 16 };

  return (
    <div
      className={`fixed z-[70] bg-zinc-950 border-2 border-orange-500 shadow-2xl flex flex-col ${fullscreen ? 'rounded-none' : ''}`}
      style={containerStyle}
      data-testid="jitsi-room"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-black border-b border-orange-500/40 flex-shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Radio className="w-3.5 h-3.5 text-red-500 animate-pulse flex-shrink-0" />
          <span className="text-[11px] font-black uppercase tracking-wider text-white truncate">
            {title || 'Live Session'}
          </span>
          {pinned && (
            <span className="text-[9px] font-bold uppercase text-orange-400 border border-orange-500/60 px-1.5 py-0.5 rounded flex-shrink-0">
              Presenter
            </span>
          )}
          {recording && (
            <span className="text-[9px] font-bold uppercase text-white bg-red-600 px-1.5 py-0.5 rounded flex-shrink-0 flex items-center gap-1">
              <Circle className="w-2 h-2 fill-current animate-pulse" /> REC
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!minimized && (
            <>
              {/* Device picker */}
              <div className="relative">
                <button
                  onClick={() => setDevicesOpen((o) => !o)}
                  className="flex items-center gap-1 text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5"
                  data-testid="jitsi-devices"
                  title="Camera & Microphone"
                >
                  <Camera className="w-3.5 h-3.5" />
                  <ChevronDown className="w-3 h-3" />
                </button>
                {devicesOpen && (
                  <div className="absolute right-0 top-full mt-1 w-72 bg-zinc-950 border border-orange-500/40 shadow-2xl rounded p-2 z-[80] text-white">
                    <div className="text-[10px] uppercase tracking-wider text-orange-400 font-bold mb-1 flex items-center gap-1">
                      <Camera className="w-3 h-3" /> Camera
                    </div>
                    <div className="max-h-40 overflow-y-auto mb-2">
                      {devices.videoInput.length === 0 && (
                        <div className="text-[11px] text-gray-500 px-2 py-1">No cameras detected</div>
                      )}
                      {devices.videoInput.map((d) => (
                        <button
                          key={d.deviceId}
                          onClick={() => pickCamera(d.deviceId, d.label)}
                          className={`w-full text-left text-[11px] px-2 py-1 rounded truncate ${selectedCam === d.deviceId ? 'bg-orange-500 text-black font-bold' : 'text-gray-300 hover:bg-white/10'}`}
                          data-testid={`jitsi-cam-option-${d.deviceId}`}
                        >
                          {d.label || 'Camera'}
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-orange-400 font-bold mb-1 flex items-center gap-1">
                      <Mic className="w-3 h-3" /> Microphone
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {devices.audioInput.length === 0 && (
                        <div className="text-[11px] text-gray-500 px-2 py-1">No mics detected</div>
                      )}
                      {devices.audioInput.map((d) => (
                        <button
                          key={d.deviceId}
                          onClick={() => pickMic(d.deviceId, d.label)}
                          className={`w-full text-left text-[11px] px-2 py-1 rounded truncate ${selectedMic === d.deviceId ? 'bg-orange-500 text-black font-bold' : 'text-gray-300 hover:bg-white/10'}`}
                          data-testid={`jitsi-mic-option-${d.deviceId}`}
                        >
                          {d.label || 'Microphone'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Screen share toggle */}
              <button
                onClick={toggleScreenShare}
                className={`text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5 ${sharingScreen ? 'text-orange-400' : ''}`}
                data-testid="jitsi-screenshare"
                title={sharingScreen ? 'Stop screen share' : 'Share screen / slides'}
              >
                <Monitor className="w-3.5 h-3.5" />
              </button>
              {/* Class recording (screen + mic → Class Recordings panel) — desktop only.
                  Promoted to a labeled, high-contrast button so it's unmissable. */}
              {canRecord && (
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={uploading}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-black uppercase tracking-wider transition-colors border
                  ${recording
                    ? 'bg-red-500/20 border-red-500 text-red-300 animate-pulse'
                    : uploading
                      ? 'bg-slate-700 border-slate-600 text-slate-300 opacity-70 cursor-wait'
                      : 'bg-red-500/10 border-red-500/60 text-red-300 hover:bg-red-500/25 hover:border-red-400'}`}
                data-testid="jitsi-record"
                title={uploading ? 'Uploading to Class Recordings…' : recording ? 'Stop & save to Class Recordings' : 'Start recording this class to the Class Recordings panel'}
              >
                {uploading ? (
                  <>
                    <Upload className="w-3 h-3 animate-pulse" /> Uploading…
                  </>
                ) : recording ? (
                  <>
                    <Square className="w-3 h-3 fill-current" /> Stop & Save
                  </>
                ) : (
                  <>
                    <Circle className="w-3 h-3 fill-red-500" /> Record
                  </>
                )}
              </button>
              )}
              {/* Presenter pin toggle */}
              <button
                onClick={togglePin}
                className={`text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5 ${pinned ? 'text-orange-400' : ''}`}
                data-testid="jitsi-presenter-pin"
                title={pinned ? 'Unpin presenter' : 'Pin me as presenter'}
              >
                {pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
              {/* Fullscreen toggle */}
              <button
                onClick={() => setFullscreen((f) => !f)}
                className="text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5"
                data-testid="jitsi-fullscreen"
                title={fullscreen ? 'Exit full screen' : 'Full screen'}
              >
                {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </button>
            </>
          )}
          <button
            onClick={() => { setMinimized((m) => !m); if (!minimized) setDevicesOpen(false); }}
            className="text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5"
            data-testid="jitsi-minimize"
            title={minimized ? 'Expand' : 'Minimize'}
          >
            {minimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onClose && onClose()}
            className="text-gray-400 hover:text-red-400 px-1.5 py-0.5 rounded hover:bg-white/5"
            data-testid="jitsi-close"
            title="Leave"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Keep the frame mounted (only visually collapsed) so the call is not destroyed on minimize */}
      <div
        ref={frameRef}
        className="flex-1 bg-black"
        style={{ display: minimized ? 'none' : 'block' }}
        data-testid="jitsi-frame"
      />
    </div>
  );
};

export default JitsiRoom;
